// =============================================================================
// RunsTable + PackoutTiles — the register's table view and the card's figures
// (plan §2D items 2 and 5; Jason, 2026-09-07: card and table views with a
// persisted toggle; both packouts side by side at equal weight).
//
// The two packouts answer different questions and are labelled as such:
// "of tipped" (packed ÷ tipped, the ops figure) and "grower" (packed ÷ packed +
// rejects + waste, what the grower is told). A big run with no reject evidence
// shows "rejects not captured" in the grower slot rather than a confident 100 %.
// Every figure comes from runCells() in src/lib/runsRegister.ts; these
// components only lay them out.
// =============================================================================
import clsx from 'clsx'
import { StatusPill } from '@/components/ui'
import TableScroll from '@/components/TableScroll'
import { useTableSort } from '@/hooks/useTableSort'
import SortableTh from '@/components/SortableTh'
import RowOpenButton from '@/components/RowOpenButton'
import { RunActionButtons } from './RunActionButtons'
import { canArchiveRun, type RunActionKind, type RunStatus } from '@/lib/runStatus'
import { fmtDateZA } from '@/lib/dates'
import { runCells, runChips, RUN_STATUS_CHIPS, RUN_TABLE_ACCESSORS, type RunTableRow, type RunChipInput } from '@/lib/runsRegister'

export type RegisterRun = RunTableRow & RunChipInput & { verified_at?: string | null }

/** UF-10: manager actions on each row. Absent for a viewer who may not act. */
export interface RunTableActions {
  isAdmin:  boolean
  busy:     boolean
  onAction: (kind: RunActionKind, runId: string) => void
}

// A run status as the register's filter chips word it, never the raw enum.
export function runStatusLabel(status: string): string {
  return RUN_STATUS_CHIPS.find(c => c.value === status)?.label ?? status
}

const growerCls = (withheld: boolean) =>
  withheld ? 'text-amber-300 text-caption' : 'text-fg'

/** The card's figures: three counts, then the two packouts side by side at equal weight. */
export function PackoutTiles({ run }: { run: RunTableRow }) {
  const c = runCells(run, fmtDateZA)
  const tiles: [string, string, string][] = [
    ['Tipped',  `${c.tipped} kg`, 'text-fg'],
    ['Packed',  `${c.packed} kg`, 'text-fg'],
    ['Pallets', c.pallets,        'text-fg'],
  ]
  const packouts: [string, string, string][] = [
    ['Packout · of tipped', c.ofTipped, c.lowOfTipped ? 'text-amber-400' : 'text-fg'],
    ['Packout · grower',    c.grower,   growerCls(c.growerWithheld)],
  ]
  const tile = ([label, val, cls]: [string, string, string]) => (
    <div key={label} className="surface-raised rounded-xl py-2 text-center">
      <p className="text-caption text-fg-muted">{label}</p>
      <p className={clsx('font-bold text-sm tabular-nums', cls)}>{val}</p>
    </div>
  )
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-3 gap-2">{tiles.map(tile)}</div>
      <div className="grid grid-cols-2 gap-2">{packouts.map(tile)}</div>
    </div>
  )
}

export function RunChips({ run, prepacked }: { run: RunChipInput; prepacked: ReadonlySet<string> }) {
  const chips = runChips(run, prepacked)
  if (chips.length === 0) return null
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {chips.map(c => (
        <span key={c} className="text-caption uppercase tracking-wider rounded-full px-2 py-0.5 bg-cyan-900/50 text-cyan-300">{c}</span>
      ))}
    </div>
  )
}

const th = 'px-2 py-2 text-caption font-semibold uppercase tracking-wider text-muted whitespace-nowrap'
const num = 'px-2 py-1.5 text-right tabular-nums'

function RunRow({ r, prepacked, onOpen, actions }: { r: RegisterRun; prepacked: ReadonlySet<string>; onOpen: (id: string) => void; actions?: RunTableActions }) {
  const c = runCells(r, fmtDateZA)
  return (
    <tr className="border-t hairline hover:bg-surface/60">
      <td className="px-2 py-1.5 whitespace-nowrap">
        <RowOpenButton label={`Open ${r.run_number}`} onOpen={() => onOpen(r.run_id)}
          className="h-11 inline-flex items-center rounded-lg px-1 -ml-1 font-semibold text-fg hover:bg-surface">
          {r.run_number}
        </RowOpenButton>
        <RunChips run={r} prepacked={prepacked} />
      </td>
      <td className="px-2 py-1.5">{r.grower}</td>
      <td className="px-2 py-1.5">{c.variety}</td>
      <td className="px-2 py-1.5">{c.puc}</td>
      <td className="px-2 py-1.5"><span className="inline-flex flex-wrap items-center gap-1"><StatusPill object="run" status={r.status} label={runStatusLabel(r.status)} />{r.verified_at && <StatusPill object="run" status="VERIFIED" />}</span></td>
      <td className="px-2 py-1.5 whitespace-nowrap">
        {c.started}
        {c.open && <span className="block text-caption text-fg-muted">{c.open}</span>}
      </td>
      <td className={num}>{c.tipped}</td>
      <td className={num}>{c.packed}</td>
      <td className={num}>{c.pallets}</td>
      <td className={clsx(num, c.lowOfTipped && 'text-amber-400')}>{c.ofTipped}</td>
      <td className={clsx(num, growerCls(c.growerWithheld))}>{c.grower}</td>
      {actions && (
        <td className="px-2 py-1.5 min-w-[200px]">
          <RunActionButtons status={r.status as RunStatus} runNumber={r.run_number}
            isAdmin={actions.isAdmin} isManager canArchive={canArchiveRun(r)} busy={actions.busy}
            onAction={kind => actions.onAction(kind, r.run_id)} />
        </td>
      )}
    </tr>
  )
}

const COLUMNS: [string, string, 'text-left' | 'text-right'][] = [
  ['run_number',     'Run',                 'text-left'],
  ['grower',         'Grower',              'text-left'],
  ['variety',        'Variety',             'text-left'],
  ['puc',            'PUC',                 'text-left'],
  ['status',         'Status',              'text-left'],
  ['started_at',     'Started',             'text-left'],
  ['tipped',         'Tipped kg',           'text-right'],
  ['packed',         'Packed kg',           'text-right'],
  ['pallets',        'Pallets',             'text-right'],
  ['packout',        'Packout · of tipped', 'text-right'],
  ['grower_packout', 'Packout · grower',    'text-right'],
]

/** Click-to-sort table over the already-fetched register rows. */
export function RunsTable({ rows, prepacked, onOpen, actions }: {
  rows:      RegisterRun[]
  prepacked: ReadonlySet<string>
  onOpen:    (id: string) => void
  actions?:  RunTableActions
}) {
  const sort = useTableSort(rows, RUN_TABLE_ACCESSORS, { key: 'started_at', dir: 'desc' })
  return (
    <TableScroll className="-mx-4 px-4">
      <table className="min-w-[980px] w-full text-sm">
        <thead>
          <tr>
            {COLUMNS.map(([key, label, align]) => (
              <SortableTh key={key} hook={sort} sortKey={key} className={clsx(th, align)}>{label}</SortableTh>
            ))}
            {actions && <th className={clsx(th, 'text-left')}>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {sort.sorted.map(r => <RunRow key={r.run_id} r={r} prepacked={prepacked} onOpen={onOpen} actions={actions} />)}
        </tbody>
      </table>
    </TableScroll>
  )
}
